import React, { useState, useEffect } from "react";
import { Outlet } from "react-router";
import { useNavigate } from "react-router-dom";

import { Layout, Menu, theme, message, Modal, Avatar, Tooltip } from "antd";
import { UserOutlined } from "@ant-design/icons";
import useAuth from "../hooks/useAuth";

import {
  Bus,
  Map,
  BookUser,
  BedDouble,
  BusFront,
  Landmark,
  Building2,
  MapPinned,
  WalletCards,
  LogOut,
  Coins,
  HandCoins,
  History,
  Smartphone,
  Video,
  Mail,
  BarChart3,
  Wallet,
  CalendarDays,
} from "lucide-react";

const { Content, Footer, Sider } = Layout;

function getItem(label, key, icon, children) {
  return {
    key,
    icon,
    children,
    label,
  };
}

const apiUrl = `${process.env.REACT_APP_API_URL}admin/basics/`;

const App = () => {
  const navigate = useNavigate();
  const { getUser } = useAuth();

  const [collapsed, setCollapsed] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const currentUser = getUser();
    setUser(currentUser);
  }, []);

  const items = [
    getItem("Calendario", "/calendario", <CalendarDays size={16} />),
    getItem("Excursiones", "/excursiones", <Map size={16} />),
    getItem("Grupos", "/grupos", <Bus size={16} />),
    getItem("Pasajeros", "/pasajeros", <BookUser size={16} />),
    getItem("Mails Vendedores", "/historial-emails", <Mail size={16} />),
    {
      key: "deudas-folder",
      label: "Deudas",
      icon: <HandCoins size={16} />,
      children: [
        getItem("Deudas Vendedores", "/transfers/debts/sellers", <Coins size={14} />),
        getItem("Deudas Guías", "/transfers/debts/guides", <Coins size={14} />),
        getItem("Rendiciones Coordinadores", "/transfers/debts/coordinators", <Coins size={14} />),
        getItem("Rendiciones Empresas", "/transfers/debts/companies", <Building2 size={14} />),
      ],
    },
    {
      key: "caja-folder",
      label: "Caja",
      icon: <History size={16} />,
      children: [
        getItem("Ganancia por micro", "/analytics/buses", <BarChart3 size={14} />),
        getItem("Gastos e Ingresos", "/analytics/expenses", <Wallet size={14} />),
        getItem("Caja", "/caja", <Wallet size={14} />),
      ],
    },
    {
      type: "divider",
    },
    {
      key: "basic",
      label: "Básicos",
      children: [
        {
          key: "users",
          icon: <UserOutlined />,
          label: "Usuarios",
          onClick: (e) => {
            window.open(
              `${process.env.REACT_APP_API_URL}admin/users/customuser/`,
              "_blank"
            );
          },
        },
        {
          key: "buses",
          icon: <BusFront size={16} />,
          label: "Micros",
          onClick: (e) => {
            window.open(`${apiUrl}bus/`, "_blank");
          },
        },
        {
          key: "hotels",
          icon: <BedDouble size={16} />,
          label: "Hoteles",
          onClick: (e) => {
            window.open(`${apiUrl}hotel/`, "_blank");
          },
        },
        {
          key: "companies",
          icon: <Building2 size={16} />,
          label: "Empresas",
          onClick: (e) => {
            window.open(`${apiUrl}company/`, "_blank");
          },
        },
        {
          key: "payment-methods",
          icon: <WalletCards size={16} />,
          label: "Métodos de pago",
          onClick: (e) => {
            window.open(`${apiUrl}paymentmethod/`, "_blank");
          },
        },
        {
          key: "boxes",
          icon: <Landmark size={16} />,
          label: "Cajas de dinero",
          onClick: (e) => {
            window.open(`${apiUrl}box/`, "_blank");
          },
        },
        {
          key: "excursion-type",
          icon: <MapPinned size={16} />,
          label: "Tipos de excursion",
          onClick: (e) => {
            window.open(`${apiUrl}excursiontype/`, "_blank");
          },
        },
      ],
    },
    {
      type: "divider",
    },
    {
      key: "logout",
      label: "Cuenta",
      type: "group",
      children: [
        {
          key: "logout",
          icon: <LogOut size={16} />,
          label: "Cerrar sesión",
          onClick: (e) => {
            handleLogout();
          },
        },
      ],
    },
    {
      type: "divider",
    },
    {
      key: "links",
      label: "Enlaces",
      type: "group",
      children: [
        {
          key: "app",
          icon: <Smartphone size={16} />,
          label: "App",
          onClick: () => {
            Modal.confirm({
              title: "App para guías y vendedores",
              content: (
                <div>
                  <p>En el siguiente link se encuentra la app</p>
                  <p>
                    <a
                      href="https://costa.expo.app/login"
                      class="text-blue-500 underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      https://costa.expo.app
                    </a>
                  </p>
                </div>
              ),
              okText: "Copiar enlace",
              cancelText: "Cancelar",
              async onOk() {
                try {
                  await navigator.clipboard.writeText(
                    "https://costa.expo.app/login"
                  );
                  message.success("Enlace de la app copiado al portapapeles!");
                } catch (err) {
                  message.error("No se pudo copiar el enlace.");
                  console.error("Failed to copy: ", err);
                }
              },
              onCancel() {
                // Optional: handle cancel action if needed
              },
            });
          },
        },
        {
          key: "tutorials",
          icon: <Video size={16} />,
          label: "Tutoriales",
          onClick: () => {
            window.open(
              "https://drive.google.com/drive/folders/1kMBP3e2QU-AFTxAUuOcvPYg1YX12HOJ3?usp=sharing",
              "_blank"
            );
          },
        },
      ],
    },
  ];

  function handleLogout() {
    localStorage.clear();

    navigate("/login");
  }

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  return (
    <Layout
      style={{
        minHeight: "100vh",
      }}
    >
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={(value) => setCollapsed(value)}
        style={{ display: 'flex', flexDirection: 'column' }}
      >
        <div style={{ flex: 1, overflow: 'auto' }}>
          <Menu
            theme="dark"
            defaultSelectedKeys={["/excursiones"]}
            mode="inline"
            items={items}
            onClick={({ key }) => {
              if (key.includes("/")) {
                navigate(key);
              }
            }}
          />
        </div>

        {/* Usuario logueado */}
        {user && (
          <div
            style={{
              padding: collapsed ? '12px 0' : '12px 16px',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              justifyContent: collapsed ? 'center' : 'flex-start',
              marginBottom: 48,
            }}
          >
            <Tooltip title={collapsed ? `${user.firstName} ${user.lastName}` : ''} placement="right">
              <Avatar
                size={collapsed ? 32 : 36}
                icon={<UserOutlined />}
                style={{ backgroundColor: '#1890ff', flexShrink: 0 }}
              />
            </Tooltip>
            {!collapsed && (
              <div style={{ overflow: 'hidden' }}>
                <div
                  style={{
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {user.firstName} {user.lastName}
                </div>
                <div
                  style={{
                    color: 'rgba(255, 255, 255, 0.45)',
                    fontSize: 12,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {user.email}
                </div>
              </div>
            )}
          </div>
        )}
      </Sider>
      <Layout>
        <Content className="m-5">
          {/* <Breadcrumb
            style={{
              margin: "16px 0",
            }}
          >
            <Breadcrumb.Item>User</Breadcrumb.Item>
            <Breadcrumb.Item>Bill</Breadcrumb.Item>
          </Breadcrumb> */}
          <div
            style={{
              padding: 24,
              minHeight: 360,
              background: colorBgContainer,
              borderRadius: borderRadiusLG,
            }}
          >
            <Outlet />
          </div>
        </Content>
        <Footer
          style={{
            textAlign: "center",
          }}
        >
          Costa Atlántica Turismo ©{new Date().getFullYear()} By MINDO Software
        </Footer>
      </Layout>
    </Layout>
  );
};
export default App;
